"use client";
import type { requestTravelAction } from "@/actions/requestTravel";
import { TagPrimo } from "@/components/tags/TagPrimo";
import { TagUntilGuaranteed } from "@/components/tags/TagUntilGuaranteed";
import { dayjs } from "@/lib/dayjs";
import { formatPrice } from "@/lib/formatPrice";
import clsx from "clsx";
import type { SearchDeparture, SearchTravel } from "front-search-schema/algolia";
import { useRouter } from "next/navigation";
import type { MouseEventHandler } from "react";
import { RequestTravelClient } from "./RequestTravelClient";
import { Speakers } from "./Speakers";

type Props = {
  travel: SearchTravel;
  departure: SearchDeparture;
  requestTravelAction: typeof requestTravelAction;
};
export const Departure = ({ travel, departure, requestTravelAction }: Props) => {
  const router = useRouter();
  const dateStart = dayjs(departure.dateStart);
  const dateEnd = dayjs(departure.dateEnd);
  const registrationStatus = departure.state.registrationStatus;

  const handleClick: MouseEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();

    if (registrationStatus === "open" || registrationStatus === "onDemand") {
      router.push(`/voyage/${travel.alias}-${travel.id}/${departure.id}/`);
    }
  };

  return (
    <div
      key={departure.id}
      className={clsx("flex justify-between relative", {
        "before:content-[''] before:absolute before:-inset-1 before:rounded-sm before:bg-transparent before:transition-colors hover:before:bg-bg-1 before:-z-1 z-1 cursor-pointer":
          registrationStatus === "open" || registrationStatus === "onDemand",
      })}
      onClick={handleClick}
    >
      <div className="flex flex-col @xl/side:flex-row @xl/side:items-center">
        <div className="flex items-center gap-2 @xl/side:w-48 @xl/side:shrink-0">
          <div
            className={clsx("w-2 h-2 rounded-full shrink-0", {
              "bg-ok": registrationStatus === "open",
              "bg-no": registrationStatus === "closed",
              "bg-tx-3": registrationStatus === "soon",
              "bg-medium": registrationStatus === "onDemand",
            })}
          />
          <div className="flex flex-col items-start gap-1 @xl/side:flex-col-reverse">
            {registrationStatus === "open" && (departure.untilGaranti != null || departure.garanti) && (
              <TagUntilGuaranteed garanti={departure.garanti} untilGaranti={departure.untilGaranti ?? null} />
            )}
            <p
              className={clsx("text-xs/[14px]", {
                "opacity-70": registrationStatus === "soon",
              })}
            >{`${dateStart.format("DD MMM YYYY")} - ${dateEnd.format("DD MMM YYYY")}`}</p>
          </div>
        </div>
        <Speakers travel={travel} departure={departure} />
      </div>
      <div className="flex flex-col items-end justify-center">
        {registrationStatus === "closed" && <p className="text-no text-base font-bold">Complet</p>}
        {(registrationStatus === "open" || registrationStatus === "onDemand") && (
          <>
            <p className="text-tx-2 text-xs/[14px]">A partir de</p>
            <p className="text-pr-1 text-[18px]/5 font-bold">
              {formatPrice(
                departure.primoPrice ? departure.defaultPrice - departure.primoPrice : departure.defaultPrice,
              )}
              <span className="font-normal text-xs">/ pers.</span>
            </p>
            {departure.primoPrice && (
              <div className="flex items-center gap-1">
                <p className="text-tx-2 text-xs whitespace-nowrap">
                  <span className="line-through">{formatPrice(departure.defaultPrice)}</span> -{" "}
                  {formatPrice(departure.primoPrice)}
                </p>
                <TagPrimo />
              </div>
            )}
          </>
        )}
        {registrationStatus === "soon" && departure.versionId && (
          <RequestTravelClient travel={travel} departure={departure} requestTravelAction={requestTravelAction} />
        )}
      </div>
    </div>
  );
};
